import * as React from 'react';
import { useMemo } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ImportDetail } from '@klareospace/pharos-lib-types';
import type { DataTableColumn } from '@klareospace/pharos-lib-web-utils';
import { Button, Card, CardContent, DataTable, ErrorBanner, TextCell } from '@klareospace/pharos-lib-web-utils';

import { hasReachedLineErrorStorageLimit, resolveRejectedRowDetails } from '../utils';
import type { RejectedRow } from '../utils';
import { useDownloadErrorReport } from '../hooks';

// ─── Constants ──────────────────────────────────────────────────────────────

const EM_DASH = '—';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ImportRejectedRowsTableProps {
  hiddenErrors: number;
  importDoc: ImportDetail;
  isLoading: boolean;
  rows: RejectedRow[];
  totalErrors: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Rejected-rows table for the import report — no pagination, at most `MAX_REPORTED_LINE_ERRORS` rows from the API. */
export function ImportRejectedRowsTable({
  hiddenErrors,
  importDoc,
  isLoading,
  rows,
  totalErrors,
}: ImportRejectedRowsTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('imports');
  const { mutate: downloadErrorReport } = useDownloadErrorReport();

  const handleDownload = (): void => {
    downloadErrorReport(importDoc);
  };

  const hasReachedStorageLimit = hasReachedLineErrorStorageLimit(importDoc);

  const columns = useMemo<DataTableColumn<RejectedRow>[]>(
    () => [
      {
        cell: (row): React.ReactNode => <TextCell>{row.lineIndex}</TextCell>,
        header: t('report.columns.rowNumber'),
        key: 'rowNumber',
      },
      {
        cell: (row): React.ReactNode => <TextCell>{row.reference ?? EM_DASH}</TextCell>,
        header: t('report.columns.order'),
        key: 'order',
      },
      {
        cell: (row): React.ReactNode => <TextCell>{row.field ?? EM_DASH}</TextCell>,
        header: t('report.columns.field'),
        key: 'field',
      },
      {
        cell: (row): React.ReactNode => <TextCell className="block max-w-[280px]">{row.value ?? EM_DASH}</TextCell>,
        header: tCommon('fields.value'),
        key: 'value',
      },
      {
        cell: (row): React.ReactNode => (
          <TextCell className="block" showTitleOnOverflow>
            {resolveRejectedRowDetails(t, row) ?? EM_DASH}
          </TextCell>
        ),
        cellClassName: 'w-full max-w-0 min-w-[200px]',
        header: t('report.columns.details'),
        key: 'details',
      },
      {
        cell: (row): React.ReactNode => <TextCell>{t(`report.errorCodes.${row.errorCode}`)}</TextCell>,
        header: t('report.columns.errorType'),
        key: 'errorType',
      },
    ],
    [t],
  );

  return (
    <Card className="rounded-xl">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <h3 className="text-sm font-semibold text-foreground">
              {t('report.rejectedRows.title', { count: totalErrors })}
            </h3>
          </div>
          <Button disabled={totalErrors === 0} onClick={handleDownload} size="sm" type="button" variant="outline">
            <Download className="h-3.5 w-3.5" />
            {t('report.rejectedRows.downloadButton')}
          </Button>
        </div>

        <DataTable<RejectedRow>
          ariaLabel={t('report.rejectedRows.title', { count: totalErrors })}
          columns={columns}
          emptyMessage={t('report.rejectedRows.empty')}
          isLoading={isLoading}
          rowKey={(row): string => `${row.lineIndex}-${row.field ?? ''}-${row.errorCode}`}
          rows={rows}
          tableClassName="w-full"
        />

        {hiddenErrors > 0 && (
          <p className="text-center text-xs text-muted-foreground">
            {t('report.rejectedRows.moreRows', { count: hiddenErrors })}
          </p>
        )}

        {hasReachedStorageLimit && <ErrorBanner message={t('report.truncatedWarning')} />}
      </CardContent>
    </Card>
  );
}
